import { ContentCopy } from "@mui/icons-material";
import { Alert, Box, Chip, IconButton, Stack, Tooltip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { MouseEvent } from "react";
import RecordProducer from "modules/record/record-producer/components/RecordProducer";
import StudioCodeEditor from "modules/studio/components/StudioCodeEditor";
import { StudioColumnMetadata, StudioContextResponse, StudioTableMetadata } from "modules/studio/services/studio-api";
import { StudioViewMode } from "modules/studio/types/studio-types";

type StudioWorkbenchProps = {
    selectedTable: string;
    selectedRecordId: string | null;
    selectedRecord: Record<string, any> | null;
    selectedTableMetadata?: StudioTableMetadata | null;
    selectedColumn?: StudioColumnMetadata | null;
    selectedTableModel: SysTable | null;
    selectedColumnModel: SysColumn | null;
    jsonColumnModel: SysColumn;
    draftRecord: Record<string, any>;
    recordsCount: number;
    changedCount: number;
    fieldValue: string;
    recordJsonValue: string;
    activeViewMode: StudioViewMode;
    editorAvailable: boolean;
    jsonAvailable: boolean;
    packageRequired: boolean;
    currentFieldChanged: boolean;
    editorError: string | null;
    allTableModels: SysTable[];
    allColumnModels: SysColumn[];
    studioConfig: StudioContextResponse["studio"] | undefined;
    tt: (key: string, fallback: string) => string;
    translateLabel: (value?: string | null, fallback?: string) => string;
    displayRecord: (record: Record<string, any>) => string;
    onViewModeChange: (event: MouseEvent<HTMLElement>, nextMode: StudioViewMode | null) => void;
    onCopyFieldPath: () => void;
    onFieldValueChange: (value: string) => void;
    onRecordJsonChange: (value: string) => void;
    onEditorValidityChange: (error: string | null) => void;
};

const StudioWorkbench = ({
    selectedTable,
    selectedRecordId,
    selectedRecord,
    selectedTableMetadata,
    selectedColumn,
    selectedTableModel,
    selectedColumnModel,
    jsonColumnModel,
    draftRecord,
    recordsCount,
    changedCount,
    fieldValue,
    recordJsonValue,
    activeViewMode,
    editorAvailable,
    jsonAvailable,
    packageRequired,
    currentFieldChanged,
    editorError,
    allTableModels,
    allColumnModels,
    studioConfig,
    tt,
    translateLabel,
    displayRecord,
    onViewModeChange,
    onCopyFieldPath,
    onFieldValueChange,
    onRecordJsonChange,
    onEditorValidityChange,
}: StudioWorkbenchProps) => (
    <Box className="platform-studio__main">
        <Box className="platform-studio__workbench-header">
            <Box minWidth={0}>
                <Typography className="platform-studio__kicker">
                    {translateLabel(selectedTableMetadata?.label, selectedTable)}
                </Typography>
                <Typography className="platform-studio__workbench-title" noWrap>
                    {selectedRecord ? displayRecord(selectedRecord) : tt("studio.record.new", "New record")}
                </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip size="small" label={`${recordsCount} ${tt("studio.label.records", "records")}`} />
                {changedCount > 0 ? (
                    <Chip size="small" color="warning" label={`${changedCount} ${tt("studio.label.changed", "changed")}`} />
                ) : null}
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={activeViewMode}
                    onChange={onViewModeChange}
                    className="platform-studio__view-toggle"
                >
                    <ToggleButton value="editor" disabled={!editorAvailable}>
                        {tt("studio.view.editor", "Editor")}
                    </ToggleButton>
                    <ToggleButton value="record">
                        {tt("studio.view.record", "Record")}
                    </ToggleButton>
                    <ToggleButton value="json" disabled={!jsonAvailable}>
                        {tt("studio.view.json", "JSON")}
                    </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={tt("studio.action.copy_field_path", "Copy field path")}>
                    <span>
                        <IconButton size="small" disabled={!selectedColumn || activeViewMode !== "editor"} onClick={onCopyFieldPath}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Box>

        {packageRequired ? (
            <Alert severity="warning" className="platform-studio__package-alert">
                {tt("studio.package.required", "Select or create a Platform Package to start developing.")}
            </Alert>
        ) : null}

        <Box className="platform-studio__workbench platform-studio__workbench--record">
            <Box className="platform-studio__editor">
                {activeViewMode === "editor" ? (
                    <Box className="platform-studio__editor-meta">
                        <Box minWidth={0}>
                            <Typography>
                                {selectedColumn ? translateLabel(selectedColumn.label, selectedColumn.name) : tt("studio.label.field", "Field")}
                            </Typography>
                            <span>
                                {selectedColumn?.column_type || "TEXT"}
                                {selectedColumn?.column_subtype ? ` / ${selectedColumn.column_subtype}` : ""}
                                {currentFieldChanged ? ` / ${tt("studio.label.unsaved", "unsaved")}` : ""}
                            </span>
                        </Box>
                    </Box>
                ) : null}
                <Box className="platform-studio__code-host">
                    {activeViewMode === "editor" ? (
                        <StudioCodeEditor
                            table={selectedTableModel}
                            column={selectedColumnModel}
                            value={fieldValue}
                            record={draftRecord}
                            allTables={allTableModels}
                            allColumns={allColumnModels}
                            studioConfig={studioConfig}
                            readOnly={packageRequired || !selectedColumn}
                            onChange={onFieldValueChange}
                            onValidityChange={onEditorValidityChange}
                        />
                    ) : activeViewMode === "json" ? (
                        <StudioCodeEditor
                            table={selectedTableModel}
                            column={jsonColumnModel}
                            value={recordJsonValue}
                            record={selectedRecord || {}}
                            allTables={allTableModels}
                            allColumns={allColumnModels}
                            studioConfig={studioConfig}
                            readOnly={packageRequired || !jsonAvailable}
                            onChange={onRecordJsonChange}
                            onValidityChange={onEditorValidityChange}
                        />
                    ) : (
                        <Box className="platform-studio__record-producer-host">
                            <RecordProducer
                                key={`${selectedTable}:${selectedRecordId || "new"}`}
                                tableName={selectedTable}
                                sysId={selectedRecordId || undefined}
                            />
                        </Box>
                    )}
                </Box>
                {editorError ? <Alert severity="error" className="platform-studio__alert">{editorError}</Alert> : null}
            </Box>
        </Box>
    </Box>
);

export default StudioWorkbench;
